// Abandoned Carts — designs that sat idle without being ordered. Useful for
// follow-up campaigns and surfacing friction points in the builders. Admins
// can archive individual cards, multi-select + bulk archive, or sweep the
// whole queue with "Clear all".
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'

interface Row {
  id: string; kind: string; name: string | null; status: string
  priceCents: number | null
  proofUrl: string | null
  updatedAt: string; createdAt: string
  userId: string | null
  userEmail: string | null; customerName: string | null
}

export default function AbandonedCarts() {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [rows, setRows] = useState<Row[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const d = await apiFetch<{ items: Row[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/abandoned-carts')
      setRows(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
    finally { setLoading(false) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [])

  function toggle(id: string) {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }
  function exitSelectMode() { setSelectMode(false); setSelected(new Set()) }

  async function archiveOne(r: Row) {
    if (!confirm(`Archive ${r.customerName ?? r.userEmail ?? 'this guest'}'s draft? They can still resume it from their library if they have an account.`)) return
    setBusy(true)
    setRows((prev) => prev.filter((x) => x.id !== r.id))
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/abandoned-carts/${r.id}`, { method: 'DELETE' })
    } catch (e) { alert((e as Error).message); await load() }
    finally { setBusy(false) }
  }

  async function archiveSelected() {
    if (selected.size === 0) return
    if (!confirm(`Archive ${selected.size} draft${selected.size === 1 ? '' : 's'}? They'll disappear from this queue and from each customer's library.`)) return
    const ids = Array.from(selected)
    setBusy(true)
    setRows((prev) => prev.filter((x) => !selected.has(x.id)))
    try {
      await apiFetch<{ archived: number; skipped: number }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/abandoned-carts/bulk-archive', {
        method: 'POST', body: JSON.stringify({ ids }),
      })
    } catch (e) { alert((e as Error).message); await load() }
    finally { setBusy(false); exitSelectMode() }
  }

  async function clearAll() {
    if (rows.length === 0) return
    if (!confirm(`Sweep ALL ${rows.length} abandoned draft${rows.length === 1 ? '' : 's'} into archive? They'll be gone from this page and from each customer's library. Use this for housekeeping.`)) return
    setBusy(true)
    try {
      const r = await apiFetch<{ archived: number }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/abandoned-carts/clear', { method: 'POST' })
      await load()
      alert(`Archived ${r.archived} draft${r.archived === 1 ? '' : 's'}.`)
    } catch (e) { alert((e as Error).message) }
    finally { setBusy(false); exitSelectMode() }
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Processing</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Abandoned Carts</h1>
          <p className="text-ink-2 mt-2 text-sm max-w-2xl">Designs saved but never submitted, idle for at least an hour. Reach out, find friction, or clear them out.</p>
        </div>
        {rows.length > 0 && (
          <div className="flex items-center gap-2">
            {selectMode ? (
              <>
                <span className="text-[11px] text-ink-3">{selected.size} selected</span>
                <button onClick={() => setSelected(new Set(rows.map((r) => r.id)))} className="text-[11px] text-ink-3 hover:text-ink underline">Select all</button>
                <button disabled={selected.size === 0 || busy} onClick={() => void archiveSelected()}
                  className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy ? '…' : `Archive ${selected.size || ''}`}
                </button>
                <button onClick={exitSelectMode} className="btn-outline text-xs">Done</button>
              </>
            ) : (
              <>
                <button onClick={() => setSelectMode(true)} className="btn-outline text-xs">Select</button>
                <button onClick={() => void clearAll()} disabled={busy}
                  className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
                  Clear all
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}
      {loading && rows.length === 0 && <div className="card text-center text-ink-3 text-sm">Loading…</div>}
      {!loading && rows.length === 0 ? (
        <div className="card text-center text-ink-3 text-sm">Nothing here — every recent design either got ordered or is still being edited.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => {
            const isSel = selected.has(r.id)
            return (
              <div key={r.id}
                onClick={() => selectMode && toggle(r.id)}
                className={'card relative ' + (selectMode ? 'cursor-pointer ' : '') +
                  (selectMode && isSel ? 'ring-2 ring-gold border-gold-dark' : '')}>
                {selectMode && (
                  <span className={'absolute top-2 left-2 w-5 h-5 rounded flex items-center justify-center text-[11px] font-bold shadow-sm pointer-events-none ' +
                    (isSel ? 'bg-gold text-ink border border-gold-dark' : 'bg-white border border-border text-ink-3')}>{isSel ? '✓' : ''}</span>
                )}
                <div className="flex gap-3">
                  <div className="w-20 h-20 bg-surface border border-border rounded overflow-hidden flex items-center justify-center shrink-0">
                    {r.proofUrl ? <img src={apiSrc(r.proofUrl)} className="max-w-full max-h-full object-contain" alt="" /> : <span className="text-[10px] text-ink-3">No proof</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-ink-3">{r.kind} · {r.status}</div>
                    {r.userId ? (
                      <Link to={`/admin/customers/${r.userId}`} onClick={(e) => selectMode && e.preventDefault()}
                        className="text-ink font-semibold truncate block hover:text-gold-dark">{r.customerName ?? r.userEmail ?? 'Customer'}</Link>
                    ) : r.userEmail ? (
                      <Link to={`/admin/customers/${encodeURIComponent(r.userEmail)}`} onClick={(e) => selectMode && e.preventDefault()}
                        className="text-ink font-semibold truncate block hover:text-gold-dark">{r.customerName ?? r.userEmail}</Link>
                    ) : (
                      <div className="text-ink font-semibold truncate">Guest</div>
                    )}
                    <div className="text-xs text-ink-3 truncate">{r.userEmail ?? '—'}</div>
                    {r.priceCents != null && <div className="text-sm text-ink mt-1">${(r.priceCents / 100).toFixed(2)}</div>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                  <div className="text-[11px] text-ink-3">Last touched {new Date(r.updatedAt).toLocaleString()}</div>
                  {!selectMode && (
                    <button disabled={busy} onClick={() => void archiveOne(r)} className="text-[11px] text-red-600 hover:text-red-700 disabled:opacity-50">Archive</button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
